import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Mail, ShieldCheck, Loader2 } from "lucide-react";
import { API } from "../lib/utils";
import Logo from "../components/Logo";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent. We'll respond within 24 hours.");
      setForm({ name: "", email: "", phone: "", message: "", website: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hero-mesh min-h-screen" data-testid="contact-page">
      <div className="container py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-primary/70 hover:text-secondary" data-testid="contact-back-home">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>
      </div>

      <div className="container grid gap-12 pb-24 lg:grid-cols-2 lg:gap-16">
        <div className="lg:pt-8">
          <Logo size="lg" />
          <h1 className="mt-8 font-display text-4xl font-extrabold tracking-tight text-primary md:text-5xl">
            Get Investment Scam Help Today
          </h1>
          <p className="mt-5 max-w-lg text-lg text-muted-foreground">
            Lost money to fraud? Our team provides free, confidential guidance for investment scam
            victims. Reach out and we'll respond within 24 hours.
          </p>
          <a
            href="mailto:support@recovery-leaders.com"
            className="mt-8 flex max-w-sm items-center gap-4 rounded-2xl border border-white/60 bg-white/80 p-5 shadow-soft backdrop-blur-xl hover-lift"
            data-testid="contact-email-card"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary/10"><Mail className="h-5 w-5 text-secondary" /></div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Email Us</p>
              <p className="font-semibold text-primary">support@recovery-leaders.com</p>
            </div>
          </a>
          <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-safe" /> All communications are confidential
          </div>
        </div>

        <div className="calm-card" data-testid="contact-form-card">
          <h2 className="mb-1 font-display text-2xl font-semibold">Send Us a Message</h2>
          <p className="mb-6 text-sm text-muted-foreground">All communications are confidential</p>

          {sent ? (
            <div className="rounded-xl border border-safe/20 bg-safe-light/50 p-8 text-center" data-testid="contact-success">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-safe/15"><ShieldCheck className="h-7 w-7 text-safe" /></div>
              <h3 className="mb-2 font-display text-xl font-semibold">Thank you for reaching out</h3>
              <p className="text-sm text-muted-foreground">Your message has been received. Our team will respond within 24 hours. Your information stays private and secure.</p>
              <button onClick={() => setSent(false)} className="btn-teal mt-6" data-testid="contact-send-another">Send another message</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div>
                <label className="mb-1.5 block text-sm font-semibold">Full Name</label>
                <input className="field-input" value={form.name} onChange={update("name")} placeholder="Your full name" data-testid="contact-name" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold">Email Address</label>
                <input type="email" className="field-input" value={form.email} onChange={update("email")} placeholder="you@example.com" data-testid="contact-email" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold">Phone Number</label>
                <input className="field-input" value={form.phone} onChange={update("phone")} placeholder="Optional" data-testid="contact-phone" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold">How can we help you?</label>
                <textarea rows={5} className="field-input resize-none" value={form.message} onChange={update("message")} placeholder="Tell us briefly about your situation..." data-testid="contact-message" />
              </div>
              <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={form.website} onChange={update("website")} aria-hidden />
              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="contact-submit">
                {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Sending...</> : "Send Message"}
              </button>
              <p className="text-center text-xs text-muted-foreground">
                By submitting this form, you agree to our <Link to="/privacy" className="text-secondary underline">Privacy Policy</Link> and <Link to="/terms" className="text-secondary underline">Terms of Service</Link>.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
